// src/pages/app/NewTravelRequestPage.jsx — Create travel request form
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { travelRequestsApi } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  AlertTriangle,
  Send,
  Lock,
  Globe,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CitySelect from "@/components/ui/CitySelect";

const SERVICES = [
  { id: "flight", label: "✈️ Flight" },
  { id: "hotel", label: "🏨 Hotel" },
  { id: "cab", label: "🚕 Cab" },
  { id: "train", label: "🚆 Train" },
];

const inputCls =
  "w-full px-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";
const labelCls =
  "block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide";

export default function NewTravelRequestPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [form, setForm] = useState({
    destination: "",
    origin: "",
    purpose: "",
    start_date: "",
    end_date: "",
    trip_type: "domestic",
    services: [],
    budget_estimate: "",
    cash_advance_requested: false,
    cash_advance_amount: "",
  });

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleService = (svc) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(svc)
        ? f.services.filter((s) => s !== svc)
        : [...f.services, svc],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.services.length) {
      toast.error("Select at least one service (flight, hotel, etc.)");
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      toast.error("End date must be after start date");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        budget_estimate: form.budget_estimate
          ? parseFloat(form.budget_estimate)
          : 0,
        cash_advance_amount: form.cash_advance_requested
          ? parseFloat(form.cash_advance_amount || 0)
          : 0,
      };
      const { data } = await travelRequestsApi.create(payload);
      toast.success("Travel request created!");
      setCreatedId(data.id);
      setShowSubmitModal(true);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to create travel request",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSubmit = async () => {
    if (!createdId) return;
    try {
      await travelRequestsApi.submit(createdId);
      toast.success("Submitted for manager approval!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setShowSubmitModal(false);
      navigate("/app/travel-requests");
    }
  };

  const handleSkipSubmit = () => {
    setShowSubmitModal(false);
    navigate("/app/travel-requests");
  };

  const budgetWarning =
    form.budget_estimate && parseFloat(form.budget_estimate) > 200000;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/app/travel-requests"
          className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">
            New Travel Request
          </h1>
          <p className="text-slate-500 text-sm">
            Submit a pre-travel request for the Travel Desk to arrange bookings
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Trip Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm sm:text-base">Trip Details</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-5">
            <CitySelect
              label="Origin City"
              value={form.origin}
              onChange={(val) => setForm((f) => ({ ...f, origin: val }))}
              placeholder="Search origin city (e.g. Mumbai, BOM)..."
            />
            <CitySelect
              label="Destination City"
              required
              value={form.destination}
              onChange={(cityName, cityObj) => {
                let detectedType = "domestic";
                if (cityObj) {
                  if (
                    cityObj.tier === "international" ||
                    (cityObj.country &&
                      cityObj.country.toLowerCase() !== "india")
                  ) {
                    detectedType = "international";
                  } else {
                    detectedType = "domestic";
                  }
                } else if (cityName) {
                  const lower = cityName.toLowerCase();
                  const intlKeywords = [
                    "phuket",
                    "bali",
                    "dubai",
                    "singapore",
                    "bangkok",
                    "london",
                    "paris",
                    "new york",
                    "tokyo",
                    "sydney",
                    "amsterdam",
                    "istanbul",
                    "doha",
                    "riyadh",
                    "kathmandu",
                    "colombo",
                    "male",
                    "maldives",
                    "toronto",
                    "vancouver",
                    "san francisco",
                    "chicago",
                    "los angeles",
                    "frankfurt",
                    "munich",
                    "zurich",
                    "milan",
                    "rome",
                    "barcelona",
                    "madrid",
                    "seoul",
                    "hong kong",
                    "kuala lumpur",
                    "ho chi minh",
                    "hanoi",
                    "manila",
                    "boracay",
                    "penang",
                    "chiang mai",
                    "pattaya",
                    "samui",
                    "abu dhabi",
                    "sharjah",
                    "muscat",
                    "bahrain",
                    "kuwait",
                    "tbilisi",
                    "baku",
                    "dhaka",
                    "paro",
                    "thimphu",
                    "cairo",
                    "johannesburg",
                    "cape town",
                    "mauritius",
                    "thailand",
                    "indonesia",
                    "uae",
                    "uk",
                    "usa",
                    "canada",
                    "australia",
                  ];
                  if (intlKeywords.some((kw) => lower.includes(kw))) {
                    detectedType = "international";
                  }
                }

                setForm((f) => ({
                  ...f,
                  destination: cityName,
                  trip_type: cityName ? detectedType : f.trip_type,
                }));
              }}
              placeholder="Search destination city (e.g. Delhi, DEL)..."
            />
            <div>
              <label className={labelCls}>Departure Date *</label>
              <input
                required
                type="date"
                value={form.start_date}
                onChange={set("start_date")}
                min={new Date().toISOString().split("T")[0]}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Return Date *</label>
              <input
                required
                type="date"
                value={form.end_date}
                onChange={set("end_date")}
                min={form.start_date || new Date().toISOString().split("T")[0]}
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <div className="flex gap-4 items-center pt-1">
                {["domestic", "international"].map((t) => {
                  const isChecked = form.trip_type === t;
                  const isLocked = !!form.destination;
                  return (
                    <label
                      key={t}
                      className={cn(
                        "flex items-center gap-2 px-3.5 py-2 rounded-lg border transition-all text-sm font-medium",
                        isLocked
                          ? "cursor-not-allowed opacity-85"
                          : "cursor-pointer",
                        isChecked && form.trip_type === "international"
                          ? "border-purple-300 bg-purple-50/60 text-purple-900 font-semibold shadow-xs"
                          : isChecked
                            ? "border-blue-300 bg-blue-50/60 text-blue-900 font-semibold shadow-xs"
                            : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300",
                      )}
                    >
                      <input
                        type="radio"
                        name="trip_type"
                        value={t}
                        checked={isChecked}
                        disabled={isLocked}
                        onChange={set("trip_type")}
                        className="accent-blue-600"
                      />
                      <span className="capitalize">{t}</span>
                      {t === "international" ? (
                        <Globe className="w-3.5 h-3.5 text-purple-600 ml-0.5" />
                      ) : null}
                    </label>
                  );
                })}
              </div>
              {form.destination && (
                <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                  Trip type is automatically locked to{" "}
                  <strong className="capitalize text-slate-600">
                    {form.trip_type}
                  </strong>{" "}
                  because destination is set to{" "}
                  <span className="font-semibold text-slate-700">
                    {form.destination}
                  </span>
                  .
                </p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Purpose of Travel *</label>
              <textarea
                required
                rows={3}
                value={form.purpose}
                onChange={set("purpose")}
                placeholder="E.g. Client meeting with Tata Consultancy Services to discuss Q4 project deliverables"
                className={`${inputCls} resize-none`}
              />
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm sm:text-base">Required Services</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-3">
              Select which bookings you need the Travel Desk to arrange:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SERVICES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleService(s.id)}
                  className={cn(
                    "px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all text-center",
                    form.services.includes(s.id)
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-slate-200 text-slate-600 hover:border-slate-300",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Budget */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm sm:text-base">Budget & Advance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <label className={labelCls}>Estimated Trip Budget (₹)</label>
              <input
                type="number"
                min="0"
                step="100"
                value={form.budget_estimate}
                onChange={set("budget_estimate")}
                placeholder="50000"
                className={inputCls}
              />
              {budgetWarning && (
                <div className="mt-2 flex items-center gap-2 text-amber-700 text-xs">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Budget exceeds ₹2,00,000 — this will be flagged for additional
                  review
                </div>
              )}
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={form.cash_advance_requested}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      cash_advance_requested: e.target.checked,
                    }))
                  }
                  className="accent-blue-600 w-4 h-4"
                />
                <span className="text-sm font-medium text-slate-700">
                  Request a cash advance for this trip
                </span>
              </label>
              {form.cash_advance_requested && (
                <div>
                  <label className={labelCls}>Cash Advance Amount (₹) *</label>
                  <input
                    required
                    type="number"
                    min="1"
                    value={form.cash_advance_amount}
                    onChange={set("cash_advance_amount")}
                    placeholder="10000"
                    className={inputCls}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    This amount will be deducted from your final reimbursement
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Link
            to="/app/travel-requests"
            className="px-6 py-2.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Creating...
              </>
            ) : (
              "Create Request"
            )}
          </button>
        </div>
      </form>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-1 mx-auto sm:mx-0">
              <Send className="w-6 h-6" />
            </div>
            <AlertDialogTitle className="text-lg sm:text-xl font-bold text-slate-900">
              Request Created Successfully!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              Your travel request has been created as a draft. Would you like to
              submit it for manager approval right now?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2 sm:gap-0">
            <AlertDialogCancel
              onClick={handleSkipSubmit}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Keep as Draft
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSubmit}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              Submit for Approval
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}
